import React, { memo } from 'react';
import { Box, Button, Stack, Typography, alpha } from '@mui/material';
import { ErrorOutline, WarningAmberRounded, ArrowForwardRounded } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import type { Problem } from '../../services/timetableService';
import { fixLink } from './timetableUtils';

interface ProblemListProps {
  problems: Problem[];
  sessionId: string;
  /** Hide the link to the screen the list is already on. */
  hereFix?: Problem['fix'];
}

/** What stands between the setup and a timetable, each with a link to where it's fixed. */
const ProblemList = memo<ProblemListProps>(({ problems, sessionId, hereFix }) => (
  <Stack spacing={1}>
    {problems.map((p, i) => {
      const isError = p.severity === 'error';
      const link = p.fix !== hereFix ? fixLink(p.fix, sessionId) : null;
      return (
        <Box
          key={`${p.code}-${i}`}
          sx={(theme) => {
            const tone = isError ? theme.palette.error.main : theme.palette.warning.main;
            return {
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.25,
              px: 1.5,
              py: 1.25,
              borderRadius: '10px',
              border: '1px solid',
              borderColor: alpha(tone, 0.28),
              bgcolor: alpha(tone, 0.06),
            };
          }}
        >
          {isError
            ? <ErrorOutline fontSize="small" color="error" sx={{ mt: '1px' }} />
            : <WarningAmberRounded fontSize="small" color="warning" sx={{ mt: '1px' }} />}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600}>{p.title}</Typography>
            {p.detail && <Typography variant="caption" color="text.secondary">{p.detail}</Typography>}
          </Box>
          {link && (
            <Button
              component={RouterLink}
              to={link.to}
              size="small"
              endIcon={<ArrowForwardRounded sx={{ fontSize: '16px !important' }} />}
              sx={{ flexShrink: 0, textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              {link.label}
            </Button>
          )}
        </Box>
      );
    })}
  </Stack>
));

ProblemList.displayName = 'ProblemList';

export default ProblemList;
